import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckIcon,
  Timer as TimerIcon
} from '@mui/icons-material';

// Task categories for organization
const TASK_CATEGORIES = [
  'PREPARATION',
  'CLEANING',
  'SANITIZING',
  'ORGANIZING',
  'INSPECTION',
  'FINISHING'
];

const ChecklistEditor = ({ 
  checklist = [], 
  onChange, 
  disabled = false,
  maxTasks = 50
}) => {
  const [tasks, setTasks] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    id: '',
    title: '',
    description: '',
    required: true,
    estimatedMinutes: 5,
    category: 'CLEANING'
  });

  // Initialize tasks from prop
  useEffect(() => {
    if (Array.isArray(checklist)) {
      setTasks(checklist.map((task, index) => ({
        ...task,
        id: task.id || `task_${index}_${Date.now()}`
      })));
    }
  }, [checklist]);

  // Update parent when tasks change
  useEffect(() => {
    onChange(tasks);
  }, [tasks, onChange]);

  const generateTaskId = () => `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const resetTaskForm = () => {
    setTaskForm({
      id: generateTaskId(),
      title: '',
      description: '',
      required: true,
      estimatedMinutes: 5,
      category: 'CLEANING'
    });
  };

  const handleAddTask = () => {
    setEditingTask(null);
    resetTaskForm();
    setShowTaskDialog(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({ ...task });
    setShowTaskDialog(true);
  };

  const handleDeleteTask = (taskId) => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
  };

  const handleSaveTask = () => {
    if (!taskForm.title.trim()) {
      return;
    }

    if (editingTask) {
      // Update existing task
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === editingTask.id ? taskForm : task
        )
      );
    } else {
      // Add new task
      setTasks(prevTasks => [...prevTasks, taskForm]);
    }

    setShowTaskDialog(false);
    resetTaskForm();
    setEditingTask(null);
  };

  const handleMoveTask = (taskId, direction) => {
    setTasks(prevTasks => {
      const currentIndex = prevTasks.findIndex(task => task.id === taskId);
      if (currentIndex === -1) return prevTasks;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= prevTasks.length) return prevTasks;

      const newTasks = [...prevTasks];
      [newTasks[currentIndex], newTasks[newIndex]] = [newTasks[newIndex], newTasks[currentIndex]];
      return newTasks;
    });
  };

  const getTotalEstimatedTime = () => {
    return tasks.reduce((total, task) => total + (task.estimatedMinutes || 0), 0);
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Service Checklist</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {tasks.length} tasks • {getTotalEstimatedTime()} min
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
            disabled={disabled || tasks.length >= maxTasks}
          >
            Add Task
          </Button>
        </Box>
      </Box>

      {tasks.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No tasks defined. Add tasks to create a checklist for this service.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List dense>
            {tasks.map((task, index) => (
              <React.Fragment key={task.id}>
                <ListItem>
                  <ListItemIcon>
                    {task.required ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <UncheckIcon color="action" />
                    )}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{task.title}</Typography>
                        <Chip 
                          label={task.category} 
                          size="small" 
                          variant="outlined" 
                        />
                        {task.estimatedMinutes > 0 && (
                          <Chip
                            icon={<TimerIcon />}
                            label={`${task.estimatedMinutes}m`}
                            size="small"
                            color="default"
                          />
                        )}
                      </Box>
                    }
                    secondary={task.description}
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveTask(task.id, 'up')}
                        disabled={disabled || index === 0}
                      >
                        <DragIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditTask(task)}
                        disabled={disabled}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTask(task.id)}
                        disabled={disabled}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < tasks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Task Edit Dialog */}
      <Dialog open={showTaskDialog} onClose={() => setShowTaskDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                required
                placeholder="e.g., Clean and disinfect bathroom surfaces"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description (optional)"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details about this task..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={taskForm.category}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  {TASK_CATEGORIES.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Minutes"
                value={taskForm.estimatedMinutes}
                onChange={(e) => setTaskForm(prev => ({ 
                  ...prev, 
                  estimatedMinutes: parseInt(e.target.value) || 0 
                }))}
                inputProps={{ min: 0, max: 999 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={taskForm.required}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, required: e.target.checked }))}
                  />
                }
                label="Required task (must be completed)"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowTaskDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained">
            {editingTask ? 'Update' : 'Add'} Task
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChecklistEditor;